'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2ChecksDisplay } from '@/lib/pr-v2/check-run-presentation';
import type { CheckListItem, ChecksTone } from '@/lib/pr-v2/checks-summary';
import type { PrV2CheckRunResponse } from '@/lib/pr-v2/types';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronDown, ChevronRight, CircleDashed, CircleMinus, Loader2, Search, X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { handleLinkListNavigation } from './link-list-navigation';
import { SIDEBAR_HEADING_CLASS, SIDEBAR_HEADING_VALUE_CLASS } from './sidebar-heading-classes';
import { getInboxCheckStatusPresentation } from '@/components/inbox/pr-inbox-check-status-presentation';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { buildPrV2ChecksDisplay, classifyPrV2CheckRun, summarizePrV2ChecksDisplay } from '@/lib/pr-v2/check-run-presentation';
import { formatCheckDuration, getCheckNameParts, getChecksListItems, getChecksTone, isCheckItemAttention } from '@/lib/pr-v2/checks-summary';
import { cn } from '@/lib/utils';
type ChecksFilter = 'all' | 'attention' | 'required';
type StatusKind = ChecksTone | 'skipped';

// One icon language for the trigger and the list rows; passed uses reviewer approvals' green.
const STATUS_ICONS: Record<StatusKind, {
  Icon: LucideIcon;
  className: string;
}> = {
  failed: {
    Icon: X,
    className: 'text-destructive'
  },
  running: {
    Icon: Loader2,
    className: 'animate-spin text-muted-foreground'
  },
  waiting: {
    Icon: CircleDashed,
    className: 'text-muted-foreground'
  },
  skipped: {
    Icon: CircleMinus,
    className: 'text-muted-foreground'
  },
  passed: {
    Icon: Check,
    className: 'text-green-700'
  }
};
function StatusIcon(t0) {
  const $ = _c(6);
  const {
    kind,
    className
  } = t0;
  const {
    Icon,
    className: color
  } = STATUS_ICONS[kind];
  let t1;
  if ($[0] !== className || $[1] !== color) {
    t1 = cn("shrink-0", color, className);
    $[0] = className;
    $[1] = color;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== Icon || $[4] !== t1) {
    t2 = <Icon className={t1} aria-hidden="true" />;
    $[3] = Icon;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function getCheckItemStatusKind(item: CheckListItem): StatusKind {
  if (item.kind === 'expected') {
    return 'waiting';
  }
  const {
    check
  } = item;
  const bucket = classifyPrV2CheckRun(check);
  if (bucket === 'failed') {
    return 'failed';
  }
  if (check.status === 'in_progress') {
    return 'running';
  }
  if (bucket === 'running') {
    return 'waiting';
  }
  return check.conclusion === 'neutral' || check.conclusion === 'skipped' ? 'skipped' : 'passed';
}
function checkStatusText(check: PrV2CheckRunResponse): string {
  if (check.status === 'in_progress') {
    return 'Running';
  }
  if (check.status === 'queued') {
    return 'Queued';
  }
  if (check.status !== 'completed') {
    return 'Pending';
  }
  const conclusions: Record<string, string> = {
    success: 'Passed',
    failure: 'Failed',
    cancelled: 'Cancelled',
    timed_out: 'Timed out',
    action_required: 'Action required',
    startup_failure: 'Failed to start',
    stale: 'Stale',
    neutral: 'Neutral',
    skipped: 'Skipped'
  };
  return check.conclusion ? conclusions[check.conclusion] ?? 'Completed' : 'Completed';
}
function CheckRow(t0) {
  const $ = _c(35);
  const {
    item
  } = t0;
  const name = item.kind === "expected" ? item.name : item.check.name;
  let t1;
  if ($[0] !== item.check || $[1] !== item.kind) {
    t1 = item.kind === "expected" ? "Expected" : checkStatusText(item.check);
    $[0] = item.check;
    $[1] = item.kind;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const status = t1;
  const detailsUrl = item.kind === "run" ? item.check.detailsUrl : null;
  let t2;
  if ($[3] !== detailsUrl || $[4] !== name) {
    t2 = getCheckNameParts({
      name,
      detailsUrl
    });
    $[3] = detailsUrl;
    $[4] = name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    prefix,
    job
  } = t2;
  let t3;
  if ($[6] !== item.check || $[7] !== item.kind) {
    t3 = item.kind === "run" ? formatCheckDuration(item.check) : null;
    $[6] = item.check;
    $[7] = item.kind;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const duration = t3;
  let t4;
  if ($[9] !== item) {
    t4 = getCheckItemStatusKind(item);
    $[9] = item;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <StatusIcon kind={t4} className="size-3.5" />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== prefix) {
    t6 = prefix ? <span className="text-muted-foreground">{`${prefix} / `}</span> : null;
    $[13] = prefix;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== job || $[16] !== name || $[17] !== t6) {
    t7 = <span className="min-w-0 flex-1 truncate text-foreground" title={name}>{t6}{job}</span>;
    $[15] = job;
    $[16] = name;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = duration ?? (status === "Passed" || status === "Failed" ? "" : status);
  let t9;
  if ($[19] !== t8) {
    t9 = <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{t8}</span>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== status) {
    t10 = <span className="sr-only">{status}</span>;
    $[21] = status;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t5 || $[25] !== t7 || $[26] !== t9) {
    t11 = <>{t5}{t7}{t9}{t10}</>;
    $[23] = t10;
    $[24] = t5;
    $[25] = t7;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const row = t11;
  if (detailsUrl) {
    const t12 = `Open ${name} check details, ${status}`;
    let t13;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = cn("flex min-h-8 w-full items-center gap-2.5 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", "hover:bg-muted focus-visible:bg-muted");
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== detailsUrl || $[30] !== row || $[31] !== t12) {
      t14 = <li><a href={detailsUrl} target="_blank" rel="noopener noreferrer" aria-label={t12} className={t13}>{row}</a></li>;
      $[29] = detailsUrl;
      $[30] = row;
      $[31] = t12;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    return t14;
  }
  let t12;
  if ($[33] !== row) {
    t12 = <li><div className="flex min-h-8 w-full items-center gap-2.5 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{row}</div></li>;
    $[33] = row;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}

/** Required runs, including those not reported yet, followed by other current-head runs. */
function PrV2ChecksList(t0) {
  const $ = _c(12);
  const {
    display,
    filter: t1,
    search: t2
  } = t0;
  const filter = t1 === undefined ? "all" : t1;
  const search = t2 === undefined ? "" : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== display || $[1] !== filter || $[2] !== search) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const query = search.trim().toLocaleLowerCase();
      const items = getChecksListItems(display).filter(item => {
        if (filter === "required" && !item.required) {
          return false;
        }
        if (filter === "attention" && !isCheckItemAttention(item)) {
          return false;
        }
        const name = item.kind === "expected" ? item.name : item.check.name;
        const detailsUrl = item.kind === "run" ? item.check.detailsUrl : null;
        const {
          prefix,
          job
        } = getCheckNameParts({
          name,
          detailsUrl
        });
        const label = prefix ? `${prefix} / ${job}` : job;
        return !query || name.toLocaleLowerCase().includes(query) || label.toLocaleLowerCase().includes(query);
      });
      if (items.length === 0) {
        let t7;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <p className="px-3 py-6 text-center text-muted-foreground">No matching checks</p>;
          $[7] = t7;
        } else {
          t7 = $[7];
        }
        t6 = t7;
        break bb0;
      }
      const requiredItems = items.filter(_temp);
      const otherItems = items.filter(_temp2);
      const orderedRequiredItems = [...requiredItems.filter(isCheckItemAttention), ...requiredItems.filter(_temp3)];
      t3 = "space-y-2 p-1";
      t4 = requiredItems.length > 0 ? <section aria-label="Required checks"><div className="px-2 py-1 text-xs text-muted-foreground">Required</div><ul>{orderedRequiredItems.map(_temp4)}</ul></section> : null;
      t5 = otherItems.length > 0 ? <section aria-label="Other checks">{requiredItems.length > 0 ? <div className="px-2 py-1 text-xs text-muted-foreground">Other</div> : null}<ul>{otherItems.map(_temp5)}</ul></section> : null;
    }
    $[0] = display;
    $[1] = filter;
    $[2] = search;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t7 = <div className={t3}>{t4}{t5}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function _temp5(item_4) {
  return <CheckRow key={item_4.kind === "expected" ? `expected-${item_4.name}` : item_4.check.id} item={item_4} />;
}
function _temp4(item_3) {
  return <CheckRow key={item_3.kind === "expected" ? `expected-${item_3.name}` : item_3.check.id} item={item_3} />;
}
function _temp3(item_2) {
  return !isCheckItemAttention(item_2);
}
function _temp2(item_1) {
  return !item_1.required;
}
function _temp(item_0) {
  return item_0.required;
}
type ChecksTriggerProps = ComponentProps<'button'> & {
  tone: ChecksTone;
  count: string;
};

/** The PR sidebar's heading row, laid out like AI code's: name and › on the left, status on the right. */
function ChecksHeadingTrigger(t0) {
  const $ = _c(14);
  let count;
  let props;
  let tone;
  if ($[0] !== t0) {
    ({
      tone,
      count,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = count;
    $[2] = props;
    $[3] = tone;
  } else {
    count = $[1];
    props = $[2];
    tone = $[3];
  }
  let t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex items-center gap-1.5"><span className={SIDEBAR_HEADING_CLASS}>Checks</span><ChevronRight className="size-4 text-zinc-500 opacity-0 transition-all group-hover/checks:opacity-100 group-focus-visible/checks:opacity-100 group-data-[state=open]/checks:rotate-90 group-data-[state=open]/checks:opacity-100" aria-hidden="true" /></span>;
    t2 = cn("flex shrink-0 items-center gap-1", SIDEBAR_HEADING_VALUE_CLASS);
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== tone) {
    t3 = <StatusIcon kind={tone} className="size-3" />;
    $[6] = tone;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== count || $[9] !== t3) {
    t4 = <span className={t2}>{t3}{count}</span>;
    $[8] = count;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== t4) {
    t5 = <h3><button type="button" className="group/checks flex w-full items-center justify-between gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" {...props}>{t1}{t4}</button></h3>;
    $[11] = props;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

/** A /code PR tab property value; the property row already carries the Checks label. */
function ChecksValueTrigger(t0) {
  const $ = _c(13);
  let count;
  let props;
  let tone;
  if ($[0] !== t0) {
    ({
      tone,
      count,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = count;
    $[2] = props;
    $[3] = tone;
  } else {
    count = $[1];
    props = $[2];
    tone = $[3];
  }
  let t1;
  if ($[4] !== tone) {
    t1 = <StatusIcon kind={tone} className="size-3.5" />;
    $[4] = tone;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== count) {
    t2 = <span className="truncate">{count}</span>;
    $[6] = count;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown className="size-3.5 shrink-0 text-zinc-500 opacity-0 transition-opacity group-hover/checks:opacity-100 group-focus-visible/checks:opacity-100 group-data-[state=open]/checks:opacity-100" aria-hidden="true" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2) {
    t4 = <button type="button" className="group/checks -ml-2 inline-flex min-h-8 max-w-full items-center gap-2 rounded-md px-2 text-xs tabular-nums transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted" {...props}>{t1}{t2}{t3}</button>;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function FilterChip(t0) {
  const $ = _c(9);
  let pressed;
  let props;
  if ($[0] !== t0) {
    ({
      pressed,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = pressed;
    $[2] = props;
  } else {
    pressed = $[1];
    props = $[2];
  }
  const t1 = pressed && "bg-muted text-foreground";
  let t2;
  if ($[3] !== t1) {
    t2 = cn("rounded-md px-2 py-0.5 text-xs/5 text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", t1);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== pressed || $[6] !== props || $[7] !== t2) {
    t3 = <button type="button" aria-pressed={pressed} className={t2} {...props} />;
    $[5] = pressed;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// Shared by the full PR sidebar and embedded session panel so both keep the
// same head filtering, rerun selection, required-check counts and status rows.
export function PrV2Checks(t0) {
  const $ = _c(68);
  const {
    checks,
    headSha,
    mergeabilityStatus,
    checksLoading: t1,
    checksError: t2,
    className: t3,
    showLabel: t4
  } = t0;
  const checksLoading = t1 === undefined ? false : t1;
  const checksError = t2 === undefined ? null : t2;
  const className = t3 === undefined ? "w-full" : t3;
  const showLabel = t4 === undefined ? true : t4;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const listRef = useRef(null);
  let display;
  let t5;
  let tone;
  if ($[0] !== checks || $[1] !== headSha || $[2] !== mergeabilityStatus?.checksStatus?.requiredChecks) {
    display = buildPrV2ChecksDisplay({
      checks,
      headSha,
      requiredChecks: mergeabilityStatus?.checksStatus?.requiredChecks
    });
    tone = getChecksTone(display);
    t5 = getInboxCheckStatusPresentation(summarizePrV2ChecksDisplay(display));
    $[0] = checks;
    $[1] = headSha;
    $[2] = mergeabilityStatus?.checksStatus?.requiredChecks;
    $[3] = display;
    $[4] = t5;
    $[5] = tone;
  } else {
    display = $[3];
    t5 = $[4];
    tone = $[5];
  }
  const {
    count,
    description
  } = t5;
  const hasRuns = display.sortedChecks.length > 0;
  const hasChecks = hasRuns || display.pendingRequiredChecks.length > 0;
  const requiredPassed = display.requiredPassedCount;
  const requiredTotal = display.requiredCheckNames.size;
  const activeFilter = filter === "required" && requiredTotal === 0 ? "all" : filter;
  let t6;
  if ($[6] !== showLabel) {
    t6 = showLabel ? <h3 className={SIDEBAR_HEADING_CLASS}>Checks</h3> : null;
    $[6] = showLabel;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const heading = t6;
  const Trigger = showLabel ? ChecksHeadingTrigger : ChecksValueTrigger;
  let t7;
  if ($[8] !== tone) {
    t7 = nextOpen => {
      if (nextOpen) {
        setFilter(tone === "failed" ? "attention" : "all");
        setSearch("");
      }
      setOpen(nextOpen);
    };
    $[8] = tone;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleOpenChange = t7;
  let content;
  if (checksLoading && !hasRuns) {
    let t8;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Skeleton className="h-4 w-12" />;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    let t9;
    if ($[11] !== heading) {
      t9 = <div data-testid="pr-v2-checks-loading-skeleton" className="flex items-center justify-between gap-3">{heading}{t8}</div>;
      $[11] = heading;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    content = t9;
  } else {
    if (checksError && !hasRuns) {
      let t8;
      if ($[13] !== checksError) {
        t8 = <p className="text-[14px]/[22px] text-destructive">{checksError}</p>;
        $[13] = checksError;
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      let t9;
      if ($[15] !== heading || $[16] !== t8) {
        t9 = <div className="space-y-2">{heading}{t8}</div>;
        $[15] = heading;
        $[16] = t8;
        $[17] = t9;
      } else {
        t9 = $[17];
      }
      content = t9;
    } else {
      if (!hasChecks) {
        let t8;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <p className="text-[14px]/[22px] text-muted-foreground">No check runs</p>;
          $[18] = t8;
        } else {
          t8 = $[18];
        }
        let t9;
        if ($[19] !== heading) {
          t9 = <div className="space-y-2">{heading}{t8}</div>;
          $[19] = heading;
          $[20] = t9;
        } else {
          t9 = $[20];
        }
        content = t9;
      } else {
        const t8 = `Checks: ${description}. View check details`;
        let t9;
        if ($[21] !== Trigger || $[22] !== count || $[23] !== t8 || $[24] !== tone) {
          t9 = <PopoverTrigger asChild={true}><Trigger data-testid="pr-v2-checks-toggle" tone={tone} count={count} aria-label={t8} /></PopoverTrigger>;
          $[21] = Trigger;
          $[22] = count;
          $[23] = t8;
          $[24] = tone;
          $[25] = t9;
        } else {
          t9 = $[25];
        }
        let t10;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <Search className="mr-2 size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />;
          $[26] = t10;
        } else {
          t10 = $[26];
        }
        let t11;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = event => setSearch(event.target.value);
          $[27] = t11;
        } else {
          t11 = $[27];
        }
        let t12;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = event_0 => {
            if (event_0.nativeEvent.isComposing) {
              return;
            }
            if (event_0.key === "ArrowDown" || event_0.key === "ArrowUp") {
              handleLinkListNavigation(event_0, listRef.current);
              return;
            }
            const firstLink = listRef.current?.querySelector("a[href]");
            if (event_0.key === "Enter" && firstLink) {
              event_0.preventDefault();
              firstLink.click();
            }
          };
          $[28] = t12;
        } else {
          t12 = $[28];
        }
        let t13;
        if ($[29] !== search) {
          t13 = <div className="flex items-center border-b border-border px-3">{t10}<Input value={search} onChange={t11} placeholder={"Search checks\u2026"} aria-label="Search checks" className="h-9 rounded-none border-0 px-0 text-[13px]/5 shadow-none focus-visible:ring-0 md:text-[13px]" onKeyDown={t12} /></div>;
          $[29] = search;
          $[30] = t13;
        } else {
          t13 = $[30];
        }
        const t14 = activeFilter === "all";
        let t15;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = () => setFilter("all");
          $[31] = t15;
        } else {
          t15 = $[31];
        }
        let t16;
        if ($[32] !== t14) {
          t16 = <FilterChip pressed={t14} onClick={t15}>All checks</FilterChip>;
          $[32] = t14;
          $[33] = t16;
        } else {
          t16 = $[33];
        }
        const t17 = activeFilter === "attention";
        let t18;
        if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
          t18 = () => setFilter("attention");
          $[34] = t18;
        } else {
          t18 = $[34];
        }
        let t19;
        if ($[35] !== t17) {
          t19 = <FilterChip pressed={t17} onClick={t18}>Needs attention</FilterChip>;
          $[35] = t17;
          $[36] = t19;
        } else {
          t19 = $[36];
        }
        let t20;
        if ($[37] !== activeFilter || $[38] !== requiredPassed || $[39] !== requiredTotal) {
          t20 = requiredTotal > 0 ? <FilterChip pressed={activeFilter === "required"} onClick={() => setFilter("required")} aria-label={`Required, ${requiredPassed} of ${requiredTotal} passed`}>Required<span className="ml-1.5 tabular-nums text-muted-foreground">{`${requiredPassed}/${requiredTotal}`}</span></FilterChip> : null;
          $[37] = activeFilter;
          $[38] = requiredPassed;
          $[39] = requiredTotal;
          $[40] = t20;
        } else {
          t20 = $[40];
        }
        let t21;
        if ($[41] !== t16 || $[42] !== t19 || $[43] !== t20) {
          t21 = <div className="flex gap-1 border-b border-border px-2 py-1.5">{t16}{t19}{t20}</div>;
          $[41] = t16;
          $[42] = t19;
          $[43] = t20;
          $[44] = t21;
        } else {
          t21 = $[44];
        }
        let t22;
        let t23;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t22 = event_1 => handleLinkListNavigation(event_1, listRef.current);
          t23 = {
            maxHeight: "min(360px, calc(var(--radix-popover-content-available-height) - 96px))"
          };
          $[45] = t22;
          $[46] = t23;
        } else {
          t22 = $[45];
          t23 = $[46];
        }
        let t24;
        if ($[47] !== activeFilter || $[48] !== display || $[49] !== search) {
          t24 = <div ref={listRef} className="overflow-y-auto overflow-x-hidden" onKeyDown={t22} style={t23}><PrV2ChecksList display={display} filter={activeFilter} search={search} /></div>;
          $[47] = activeFilter;
          $[48] = display;
          $[49] = search;
          $[50] = t24;
        } else {
          t24 = $[50];
        }
        let t25;
        if ($[51] !== t13 || $[52] !== t21 || $[53] !== t24) {
          t25 = <PopoverContent align="start" collisionPadding={12} className="w-[min(440px,calc(100vw-24px))] overflow-hidden p-0"><div className="text-[13px]/5">{t13}{t21}{t24}</div></PopoverContent>;
          $[51] = t13;
          $[52] = t21;
          $[53] = t24;
          $[54] = t25;
        } else {
          t25 = $[54];
        }
        let t26;
        if ($[55] !== handleOpenChange || $[56] !== open || $[57] !== t25 || $[58] !== t9) {
          t26 = <Popover open={open} onOpenChange={handleOpenChange}>{t9}{t25}</Popover>;
          $[55] = handleOpenChange;
          $[56] = open;
          $[57] = t25;
          $[58] = t9;
          $[59] = t26;
        } else {
          t26 = $[59];
        }
        let t27;
        if ($[60] !== checksError) {
          t27 = checksError ? <p className="mt-1 text-[14px]/[22px] text-muted-foreground">Could not refresh checks</p> : null;
          $[60] = checksError;
          $[61] = t27;
        } else {
          t27 = $[61];
        }
        let t28;
        if ($[62] !== t26 || $[63] !== t27) {
          t28 = <>{t26}{t27}</>;
          $[62] = t26;
          $[63] = t27;
          $[64] = t28;
        } else {
          t28 = $[64];
        }
        content = t28;
      }
    }
  }
  let t8;
  if ($[65] !== className || $[66] !== content) {
    t8 = <section className={className} aria-label="Checks">{content}</section>;
    $[65] = className;
    $[66] = content;
    $[67] = t8;
  } else {
    t8 = $[67];
  }
  return t8;
}